import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import type { ReplayEvent, ReplayEventType } from '../../../packetsReplay/extractReplayEvents'
import { packetsReplayState, packetTimeAt } from '../../state/packetsReplayState'
import { formatDuration } from '../../../formatUnits'
import { isMobileLayout } from '../../useIsMobile'
import { RepeatIcon } from '../../settings/icons'
import styles from './InfoViewPanel.module.css'
import PlayerAvatar from './PlayerAvatar'
import CollapseTab from './CollapseTab'
import {
  ArrowLeftIcon, ArrowRightIcon, InfoIcon,
  DeathIcon, KillMobIcon, KillPlayerIcon, ChatMarkerIcon, CommandIcon, TeleportIcon, DimensionIcon
} from './PlayerIcons'

interface Props {
  replayName: string
  isPlaying: boolean
  progress: { current: number; total: number }
  totalTimeMs?: number
  replayEvents?: ReplayEvent[]
  onSeekToTime?: (ms: number, packetIndex?: number) => void
}

function extractMcText (obj: unknown): string {
  if (typeof obj === 'string') return obj
  if (!obj || typeof obj !== 'object') return ''
  const o = obj as Record<string, unknown>
  let out = typeof o.text === 'string' ? o.text : ''
  if (o.translate && Array.isArray(o.with)) {
    out = (o.with as unknown[]).map(extractMcText).join(' ')
  }
  if (Array.isArray(o.extra)) {
    out += (o.extra as unknown[]).map(extractMcText).join('')
  }
  return out.trim()
}

function cleanLabel (label: string | undefined): string {
  if (!label) return ''
  const s = label.trim()
  if (!s.startsWith('{') && !s.startsWith('[')) return s
  try {
    return extractMcText(JSON.parse(s))
  } catch {
    return ''
  }
}

// Та же палитра меток, что на таймлайне; урон и активность в макете —
// нейтральная точка, без подкраски.
const EVENT_COLOR_CLASS: Partial<Record<ReplayEventType, string>> = {
  death: 'death',
  kill_player: 'killPlayer',
  kill_mob: 'mob',
  chat: 'chat',
  command: 'command',
  dimension_change: 'dimc',
  respawn: 'dim',
  teleport: 'tp',
  afk: 'afk',
}

const EVENT_NAME: Partial<Record<ReplayEventType, string>> = {
  death: 'Гибель игрока',
  damage: 'Урон',
  kill_player: 'Убийство игрока',
  kill_mob: 'Убийство моба',
  chat: 'Сообщение',
  command: 'Команда',
  dimension_change: 'Переход измерений',
  respawn: 'Возрождение',
  teleport: 'Телепорт',
  afk: 'AFK',
  high_activity: 'Активность',
  low_activity: 'Малая активность',
}

// Событию без своей иконки макет рисует нейтральную точку (delimiter_big).
const EVENT_ICON: Partial<Record<ReplayEventType, (props: { size?: number }) => JSX.Element>> = {
  death: DeathIcon,
  kill_player: KillPlayerIcon,
  kill_mob: KillMobIcon,
  chat: ChatMarkerIcon,
  command: CommandIcon,
  teleport: TeleportIcon,
  dimension_change: DimensionIcon,
  respawn: RepeatIcon,
}

function renderEventIcon (type: ReplayEventType) {
  const Svg = EVENT_ICON[type]
  return Svg ? <Svg size={16} /> : <span className={styles.dot} />
}

function labelOfEvent (ev: ReplayEvent): string {
  const cleaned = cleanLabel(ev.label)
  if (cleaned) return cleaned
  return EVENT_NAME[ev.type] ?? ev.type
}


const NOW_WINDOW_MS = 1500 // event is highlighted as "идёт сейчас" within this many ms after fire
const RECENT_WINDOW_MS = 6000 // longer tail for "только что"

export default function InfoViewPanel ({ replayName, isPlaying, progress, totalTimeMs, replayEvents, onSeekToTime }: Props) {
  // A 300px panel would swallow a phone screen — mobile starts on the badge.
  const [hidden, setHidden] = useState(isMobileLayout)
  useEffect(() => {
    packetsReplayState.infoViewCollapsed = hidden
  }, [hidden])

  const panelRef = useRef<HTMLDivElement>(null)
  const panelWrapRef = useRef<HTMLDivElement>(null)
  // The panel is absolutely positioned so the wrap can animate between the
  // expanded size and the badge; feed the wrap the panel's natural height so
  // that transition has two concrete endpoints instead of `auto`.
  useLayoutEffect(() => {
    const panel = panelRef.current
    const wrap = panelWrapRef.current
    if (!panel || !wrap) return

    let frame = 0
    const update = () => {
      frame = 0
      wrap.style.setProperty('--panel-h', `${panel.offsetHeight}px`)
      // the chat shares this column and starts below the panel — see Chat.css
      document.documentElement.style.setProperty('--info-panel-bottom', `${wrap.getBoundingClientRect().bottom}px`)
    }
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(update)
    }

    update()
    const ro = new ResizeObserver(schedule)
    ro.observe(panel)
    // the wrap collapses to the badge over 0.28s — keep the chat in step with it
    ro.observe(wrap)
    window.addEventListener('resize', schedule)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      ro.disconnect()
      window.removeEventListener('resize', schedule)
      document.documentElement.style.removeProperty('--info-panel-bottom')
    }
  }, [])

  const playerName = (globalThis as any).bot?.username ?? null
  const hasTime = !!(totalTimeMs && totalTimeMs > 0)
  const evts = replayEvents ?? []
  const pointEvts = evts.filter(e => e.type !== 'low_activity')

  // Real packet time, not index/total — see packetTimeAt: the linear guess
  // drifts by seconds and makes «в процессе» / «через N с» lie.
  const currentMs = packetTimeAt(progress.current)
    ?? ((hasTime && progress.total > 0) ? (progress.current / progress.total) * totalTimeMs : 0)

  // Last point event whose time ≤ currentMs
  let passedIdx = -1
  for (const [i, pointEvt] of pointEvts.entries()) {
    if (pointEvt.time <= currentMs + 50) passedIdx = i
    else break
  }
  const lastPassed = passedIdx >= 0 ? pointEvts[passedIdx] : undefined
  const nextEvent = passedIdx + 1 < pointEvts.length ? pointEvts[passedIdx + 1] : undefined

  // Are we currently inside a low_activity segment?
  const insideLow = evts.find(ev => ev.type === 'low_activity'
    && typeof ev.duration === 'number'
    && ev.time <= currentMs
    && currentMs <= ev.time + ev.duration)

  // Choose the active event to display
  const sinceLast = lastPassed ? currentMs - lastPassed.time : Infinity
  const isHappeningNow = sinceLast <= NOW_WINDOW_MS
  const isRecent = sinceLast <= RECENT_WINDOW_MS

  let activeEvent: ReplayEvent | undefined
  if (isHappeningNow || isRecent) {
    activeEvent = lastPassed
  } else if (insideLow) {
    activeEvent = insideLow
  }

  // Блок «текущее событие» из макета: иконка + название, снизу статус.
  // Когда ничего не идёт — показываем ближайшее впереди («Через 5 с»).
  const shownEvent = activeEvent ?? nextEvent
  const eventName = shownEvent ? labelOfEvent(shownEvent) : ''
  const colorClass = shownEvent ? (EVENT_COLOR_CLASS[shownEvent.type] ?? '') : ''

  let eventStatus = ''
  if (activeEvent === lastPassed && lastPassed) {
    eventStatus = isHappeningNow ? 'В процессе' : `Произошло ${formatDuration(sinceLast)} назад`
  } else if (activeEvent && activeEvent === insideLow && insideLow.duration) {
    const remaining = (insideLow.time + insideLow.duration) - currentMs
    eventStatus = remaining > 0 ? `Ещё ${formatDuration(remaining)}` : 'В процессе'
  } else if (!activeEvent && nextEvent) {
    eventStatus = `Через ${formatDuration(nextEvent.time - currentMs)}`
  }

  // Bottom progress info: how many events passed / total
  const passedCount = passedIdx + 1
  const totalCount = pointEvts.length

  const goToEvent = (dir: 1 | -1) => {
    const evts = replayEvents
    if (!evts?.length || !onSeekToTime) return
    const currentMs = packetTimeAt(progress.current)
      ?? (totalTimeMs && progress.total > 0 ? (progress.current / progress.total) * totalTimeMs : 0)
    const MARGIN = 500
    let targetIdx: number
    if (dir > 0) {
      targetIdx = evts.findIndex(e => e.time > currentMs + MARGIN)
      if (targetIdx === -1) targetIdx = evts.length - 1
    } else {
      targetIdx = -1
      for (let i = evts.length - 1; i >= 0; i--) {
        if (evts[i]!.time < currentMs - MARGIN) { targetIdx = i; break }
      }
      if (targetIdx === -1) targetIdx = 0
    }
    onSeekToTime(evts[targetIdx]!.time, evts[targetIdx]!.packetIndex)
  }

  return (
    <div
      ref={panelWrapRef}
      className={[styles.panelWrap, hidden && styles.panelWrapCollapsed].filter(Boolean).join(' ')}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      data-replay-ui
    >
      {/* Panel and badge both stay mounted and cross-fade, so collapsing is a
          size transition rather than a jump cut. */}
      <div
        ref={panelRef}
        aria-hidden={hidden}
        className={[styles.panel, hidden && styles.panelFaded].filter(Boolean).join(' ')}
      >
        {/* Header — recording identity chip + packet counter */}
        <div className={styles.headerRow}>
          <span className={styles.replayName}>{replayName || 'replay'}</span>
          <span className={styles.counter} data-playing={isPlaying}>
            {progress.current}/{progress.total}
          </span>
        </div>

        {/* Recording title */}
        <div className={styles.eventTitle}>{replayName || 'Запись'}</div>

        {/* Player row */}
        {playerName && (
          <div className={styles.playerRow}>
            <span className={styles.avatar}><PlayerAvatar name={playerName} size={20} /></span>
            <span className={styles.playerName}>{playerName}</span>
          </div>
        )}

        <div className={styles.divider} />

        {/* Events nav */}
        <div className={styles.recsHeader}>
          <div>
            <div className={styles.recsLabel}>События</div>
            {hasTime && totalCount > 0 && (
              <div className={styles.recsCount}>{passedCount} / {totalCount} пройдено</div>
            )}
            {hasTime && totalCount === 0 && (
              <div className={styles.recsCount}>нет событий</div>
            )}
          </div>
          <div className={styles.navBtns}>
            <button
              className={styles.iconBtn}
              title="Предыдущее событие"
              onClick={() => goToEvent(-1)}
              disabled={!replayEvents?.length || !onSeekToTime}
            ><ArrowLeftIcon /></button>
            <button
              className={styles.iconBtn}
              title="Следующее событие"
              onClick={() => goToEvent(1)}
              disabled={!replayEvents?.length || !onSeekToTime}
            ><ArrowRightIcon /></button>
          </div>
        </div>

        {/* Current event — icon + name, status underneath */}
        {shownEvent && (
          <>
            <div className={styles.divider} />
            <div
              className={styles.currentEvent}
              data-state={isHappeningNow ? 'now' : (isRecent ? 'recent' : 'idle')}
            >
              <div className={styles.currentEventHead}>
                <span className={[styles.currentEventIcon, colorClass ? styles[colorClass as keyof typeof styles] : ''].filter(Boolean).join(' ')}>
                  {renderEventIcon(shownEvent.type)}
                </span>
                <span className={styles.currentEventName}>{eventName}</span>
              </div>
              {eventStatus && <div className={styles.currentEventStatus}>{eventStatus}</div>}
            </div>
          </>
        )}
      </div>

      <div
        aria-hidden={!hidden}
        className={[styles.collapsedTile, !hidden && styles.collapsedTileHidden].filter(Boolean).join(' ')}
      >
        <InfoIcon className={styles.collapsedIcon} />
      </div>

      {/* Collapse / expand — shared tab on the right edge. */}
      <CollapseTab
        edge="right"
        collapsed={hidden}
        onToggle={() => setHidden(v => !v)}
      />
    </div>
  )
}
